import {
  Box,
  Flex,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Spinner,
  useOutsideClick,
  Divider,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useMemo, useEffect, useRef } from 'react';

import { useGetLimitedPartnersForOrganizationLazyQueryGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { Typography } from 'packages/common-frontend/components/Typography';

export const LimitedPartnerSearch = ({
  lenderOrganizationId,
  defaultQuery = '',
  minWidth,
  onSelect,
  inputPlaceholder = '',
}: {
  lenderOrganizationId: string;
  defaultQuery?: string;
  minWidth?: string;
  onSelect: (limitedPartnerId: string, limitedPartnerName: string) => void;
  inputPlaceholder?: string;
}) => {
  const [query, setQuery] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    getLimitedPartners,
    {
      data: limitedPartnersData,
      loading: limitedPartnersLoading,
      error: getLimitedPartnersError,
    },
  ] = useGetLimitedPartnersForOrganizationLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
  });

  const limitedPartnerResults = useMemo(
    () => (limitedPartnersData ? limitedPartnersData.limitedPartners : []),
    [limitedPartnersData],
  );

  if (getLimitedPartnersError) throw getLimitedPartnersError;

  const fetchLimitedPartners = useMemo(
    () =>
      _.debounce((q: string) => {
        getLimitedPartners({
          variables: {
            organizationId: lenderOrganizationId,
            queryString: q || defaultQuery,
          },
        });
      }, 300),
    [defaultQuery, getLimitedPartners, lenderOrganizationId],
  );

  useEffect(() => {
    if (isOpen) {
      fetchLimitedPartners(query);
    }
  }, [query, fetchLimitedPartners, isOpen]);

  const boxRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: boxRef,
    handler: () => onClose(),
  });

  return (
    <Box ref={boxRef} minWidth={minWidth}>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        autoFocus={false}
        returnFocusOnClose={false}
        strategy="fixed"
        placement="bottom-end"
        closeOnBlur={false}
      >
        <PopoverTrigger>
          <SearchBarInput
            query={query}
            onChangeQuery={(q) => {
              setQuery(q);
            }}
            isDisabled={false}
            inputProps={{
              placeholder: inputPlaceholder,
              width: '100%',
            }}
            onFocus={onOpen}
          />
        </PopoverTrigger>
        <PopoverContent height="19rem" overflowY="scroll">
          <Box p="1rem" height="100%">
            {(limitedPartnersLoading || !limitedPartnersData) && (
              <Flex justifyContent="center" alignItems="center" height="100%">
                <Spinner size="lg" />
              </Flex>
            )}
            {!limitedPartnersLoading && limitedPartnersData
              ? limitedPartnerResults.map((limitedPartner, index) => {
                  const isLastResult =
                    index === limitedPartnerResults.length - 1;
                  const filteredAliases = limitedPartner.alternateAliases.filter(
                    (alias) =>
                      alias.toLowerCase() !== limitedPartner.name.toLowerCase(),
                  );
                  return (
                    <Box key={limitedPartner.id}>
                      <Flex
                        direction="column"
                        padding="0.5rem"
                        _hover={{
                          cursor: 'pointer',
                          background: colors.gray[50],
                          borderRadius: '0.25rem',
                        }}
                        onClick={() => {
                          onSelect(limitedPartner.id, limitedPartner.name);
                          setQuery(''); // Clear query on selection
                        }}
                      >
                        <Typography
                          variant="detail"
                          colorScheme="v2-aurora"
                          fontWeight="medium"
                        >
                          {limitedPartner.name}
                        </Typography>
                        {filteredAliases.length > 0 && (
                          <Flex mt="1rem" direction="column" gap="0.5rem">
                            {filteredAliases.map((alias) => (
                              <Typography
                                variant="detail-muted"
                                key={`${limitedPartner.id}-${alias}`}
                              >
                                {alias}
                              </Typography>
                            ))}
                          </Flex>
                        )}
                      </Flex>
                      {!isLastResult && <Divider m="0.5rem 0rem" />}
                    </Box>
                  );
                })
              : null}
          </Box>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
